// The job's own dialogs and its estimate card, moved out of the job sheet so the job page (JobRoute.tsx)
// opens the same ones: an estimate with its options, OK, send, link and PDF (EstimateCard, SendDialog);
// cancelling the job with its trip-charge waiver and the text to the site contact (CancelDialog); and
// cancelling one visit (CancelVisitDialog).

import { useEffect, useRef, useState } from 'react';
import { dateText, windowText } from '@flowology/kernel';
import { client } from '../api.ts';
import { Money, errorText } from '../books/ui.tsx';
import { Loading } from '../shared/Loading.tsx';
import { SaveButton, useSaveKey } from '../shared/SaveButton.tsx';
import { money, type JobDetail } from '../review/review-model.ts';
import { phoneText, visitTextTo, type Customer } from '../types.ts';
import {
  BOOK_PERMS,
  bookable,
  CANCEL_REASONS,
  cancelInvoiceWords,
  cancelReason,
  cancelText,
  visitToCancel,
  ESTIMATE_BUILD_PERMS,
  beforeTaxText,
  estimateState,
  holds,
  lineTagsText,
  soldByText,
  PRICE_APPROVE_PERMS,
  SEND_PERMS,
  sendable,
  marginText,
  waitingLines,
  type Estimate,
} from './job-model.ts';
import { stEstimateWords } from '../history/estimates-model.ts';
import { qtyText } from '../invoice/invoice-model.ts';
import './bp-sheets.css';
import { AsLowAs } from '../shared/AsLowAs.tsx';
import '../review/review.css';
import './job.css';
import { plainText } from '../shared/plain-text.ts';

export function EstimateCard({
  estimate: e,
  held,
  mirroring,
  jobLive,
  unbooked,
  onEdit,
  onSend,
  onBook,
  onApproved,
}: {
  estimate: Estimate;
  held: readonly string[];
  /** ServiceTitan's while the company mirrors it: no edit, send, price OK or booking; link and PDF stay. */
  mirroring: boolean;
  jobLive: boolean;
  /** Sold for later and still waiting in To book. */
  unbooked: boolean;
  onEdit: () => void;
  onSend: () => void;
  onBook: () => void;
  onApproved: (optionTitle: string) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [why, setWhy] = useState<{ optionId: string; text: string } | null>(null);
  const canApprove = holds(held, PRICE_APPROVE_PERMS) && !e.frozen && !mirroring;
  // PDF and Copy link (M4), as the invoice has them (review/InvoiceActions.tsx)
  const [sharing, setSharing] = useState<'pdf' | 'link' | null>(null);
  const [shared, setShared] = useState<{ note: string | null; link: string | null; error: string | null }>({
    note: null,
    link: null,
    error: null,
  });
  const pdf = async () => {
    setSharing('pdf');
    setShared({ note: null, link: null, error: null });
    try {
      const r = await client.call<{ filename: string; base64: string }>('documents.estimatePdf', {
        estimateId: e.id,
      });
      const a = document.createElement('a');
      a.href = `data:application/pdf;base64,${r.base64}`;
      a.download = r.filename;
      a.click();
    } catch (err) {
      setShared({ note: null, link: null, error: errorText(err) });
    } finally {
      setSharing(null);
    }
  };
  const copyLink = async () => {
    setSharing('link');
    setShared({ note: null, link: null, error: null });
    try {
      const r = await client.call<{ url: string; expiry: string }>('estimates.copyLink', {
        estimateId: e.id,
      });
      // Where the browser won't write to the clipboard, the link stays on screen to copy by hand.
      const copied = await navigator.clipboard?.writeText(r.url).then(
        () => true,
        () => false,
      );
      setShared({
        note: copied ? `Link copied. It ${r.expiry}.` : `Copy the link below. It ${r.expiry}.`,
        link: r.url,
        error: null,
      });
    } catch (err) {
      setShared({ note: null, link: null, error: errorText(err) });
    } finally {
      setSharing(null);
    }
  };
  const approve = async (optionId: string, title: string) => {
    setBusy(optionId);
    setWhy(null);
    try {
      await client.call('estimates.approvePrice', { estimateId: e.id, optionId });
      onApproved(title);
    } catch (err) {
      setWhy({ optionId, text: errorText(err) });
    } finally {
      setBusy(null);
    }
  };
  const sold = e.status === 'sold';
  return (
    <article className="stone est-card">
      <div className="row between">
        <div>
          <h4>{e.title}</h4>
          <span className="muted small">
            Estimate {e.number}
            {/* ServiceTitan's while mirroring: no Edit or Send (history fix 1) */}
            {/* and one ServiceTitan took off says so, whatever the mode (history fix 2) */}
            {stEstimateWords(e, mirroring).map((w) => ` · ${w}`)}
            {soldByText(e.soldBy) && ` · ${soldByText(e.soldBy)}`}
          </span>
        </div>
        <span className={`chip${sold ? ' ok' : e.status === 'sent' ? ' brand' : ''}`}>
          {estimateState(e)}
        </span>
      </div>
      <ul className="est-opts">
        {e.options.map((o) => {
          const waiting = waitingLines(o);
          const picked = sold && e.selectedOptionId === o.id;
          return (
            <li key={o.id} className={picked ? 'picked' : undefined}>
              <div className="row between">
                <span>
                  <b>{o.title}</b>
                  {picked && <span className="chip ok">Chosen</span>}
                </span>
                <Money cents={o.totalCents} />
              </div>
              {/* the total with tax, as ServiceTitan shows it; the amount before tax beneath (history fix 1) */}
              {beforeTaxText(o) && <p className="muted small est-before-tax">{beforeTaxText(o)}</p>}
              {o.summary && <p className="muted small">{o.summary}</p>}
              {/* its lines in ServiceTitan's order, each with its item's code (history fix 2) */}
              {e.imported && o.lines.length > 0 && (
                <details className="est-lines small">
                  <summary>Its lines ({o.lines.length})</summary>
                  <ul>
                    {o.lines.map((l) => (
                      <li key={l.id}>
                        {l.description}
                        {lineTagsText(l) && <span className="muted"> · {lineTagsText(l)}</span>}
                        <span className="muted">
                          {' '}
                          · {qtyText(l.quantity)} × <Money cents={l.unitPriceCents} />
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {/* BL-005: the monthly payment the company's financing plans give */}
              <AsLowAs totalCents={o.totalCents} />
              {(() => {
                const m = marginText(o);
                return m && <p className={`small est-margin${m.under ? ' under' : ''}`}>{m.text}</p>;
              })()}
              {(waiting.length > 0 || o.margin?.waits) && !e.frozen && (
                <div className="est-wait">
                  <p className="small">
                    <b>Waits for the office’s OK</b> before it can be signed:
                  </p>
                  <ul>
                    {o.margin?.waits && <li className="small">Its margin is under the company’s minimum.</li>}
                    {e.waitingSale?.optionId === o.id && (
                      <li className="small">
                        <b>{e.waitingSale.signerName} signed it.</b> When it’s OK’d, the sale goes through.
                      </li>
                    )}
                    {waiting.map((l) => (
                      <li key={l.id} className="small">
                        {l.description}:{' '}
                        {l.bookPriceCents !== null ? (
                          <>
                            <s>
                              <Money cents={l.bookPriceCents} />
                            </s>{' '}
                            <Money cents={l.unitPriceCents} />
                          </>
                        ) : (
                          <Money cents={l.unitPriceCents} />
                        )}
                        {l.overrideReason && <span className="muted"> · “{l.overrideReason}”</span>}
                      </li>
                    ))}
                  </ul>
                  {canApprove && (
                    <SaveButton
                      busy={busy === o.id}
                      busyLabel="OK’ing…"
                      error={why?.optionId === o.id ? why.text : null}
                      className="btn primary"
                      onClick={() => void approve(o.id, o.title)}
                    >
                      OK the price
                    </SaveButton>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {e.signature && (
        <p className="muted small">
          Signed by {e.signature.signerName}
          {e.disposition === 'sold_later' ? ' · for later' : ''}
        </p>
      )}
      <div className="row">
        {!e.frozen && jobLive && holds(held, ESTIMATE_BUILD_PERMS) && !mirroring && (
          <button type="button" className="btn" onClick={onEdit}>
            Edit
          </button>
        )}
        {sendable(e) && jobLive && holds(held, SEND_PERMS) && (
          <>
            {!mirroring && (
              <button type="button" className="btn" onClick={onSend}>
                {e.status === 'sent' ? 'Send again' : 'Send to the customer'}
              </button>
            )}
            <button type="button" className="btn" disabled={!!sharing} onClick={() => void copyLink()}>
              {sharing === 'link' ? 'Making the link…' : 'Copy link'}
            </button>
          </>
        )}
        {e.options.length > 0 && (
          <button type="button" className="btn" disabled={!!sharing} onClick={() => void pdf()}>
            {sharing === 'pdf' ? 'Making the PDF…' : 'PDF'}
          </button>
        )}
        {bookable(e) && unbooked && holds(held, BOOK_PERMS) && !mirroring && (
          <button type="button" className="btn primary" onClick={onBook}>
            Book the work
          </button>
        )}
      </div>
      {shared.note && (
        <p className="notice ok" role="status">
          {shared.note}
        </p>
      )}
      {shared.link && (
        <label className="f">
          Link to the estimate
          <input className="field" readOnly value={shared.link} onFocus={(ev) => ev.target.select()} />
        </label>
      )}
      {shared.error && (
        <p className="error" role="alert">
          {shared.error}
        </p>
      )}
    </article>
  );
}

// ── sending ──

/** Why a contact method can't take the estimate, or null when it can. */
function notReachable(c: Customer, m: Customer['contacts'][number]['methods'][number]): string | null {
  if (c.doNotContact) return 'the customer is do-not-contact';
  if (m.doNotContact) return 'do not contact';
  if (!m.consent) return m.kind === 'phone' ? 'no consent to texts' : 'no consent to email';
  return null;
}

export function SendDialog({
  estimate,
  customerId,
  onClose,
  onSent,
}: {
  estimate: Estimate;
  customerId: string;
  onClose: () => void;
  onSent: (count: number) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = useSaveKey(`send:${estimate.id}:${picked.join(',')}`);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    client
      .call<Customer>('customers.get', { id: customerId })
      .then((c) => {
        setCustomer(c);
        // The first contact that may be texted, else emailed, is ticked.
        const ok = c.contacts.flatMap((k) => k.methods).filter((m) => !notReachable(c, m));
        const first = ok.find((m) => m.kind === 'phone' && m.isPrimary) ?? ok[0];
        if (first) setPicked([first.id]);
      })
      .catch((e: unknown) => setLoadError(errorText(e, 'Could not load the customer’s contacts.')));
  }, [customerId]);
  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ sent: unknown[] }>(
        'estimates.send',
        { estimateId: estimate.id, contactMethodIds: picked },
        { idempotencyKey: save.key },
      );
      onSent(r.sent.length);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <dialog ref={ref} className="sheet ask" aria-labelledby="send-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy && picked.length) void send();
        }}
      >
        <h3 id="send-title">Send estimate {estimate.number}</h3>
        <p className="muted">
          A link to see the options and sign online. A signature online is always sold for later: the office
          books the work.
        </p>
        {loadError && <p className="error">{loadError}</p>}
        {!customer && !loadError && <Loading error={null} what="the contacts" />}
        {customer && (
          <fieldset className="send-to">
            <legend className="flabel">Send to (up to 3)</legend>
            {customer.contacts.flatMap((k) =>
              k.methods.map((m) => {
                const no = notReachable(customer, m);
                const on = picked.includes(m.id);
                return (
                  <label key={m.id} className={`checkrow${no ? ' off' : ''}`}>
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={!!no || (!on && picked.length >= 3)}
                      onChange={() => setPicked((p) => (on ? p.filter((x) => x !== m.id) : [...p, m.id]))}
                    />
                    <span>
                      {k.name} · {m.kind === 'phone' ? `text ${phoneText(m.value)}` : `email ${m.value}`}
                      {no && <span className="muted small"> · {no}</span>}
                    </span>
                  </label>
                );
              }),
            )}
            {!customer.contacts.some((k) => k.methods.some((m) => !notReachable(customer, m))) && (
              <p className="notice warn">
                Nobody on this customer may be texted or emailed. Call them instead.
              </p>
            )}
          </fieldset>
        )}
        <div className="row end">
          <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
            Cancel
          </button>
          <SaveButton
            busy={busy}
            busyLabel="Sending…"
            error={error}
            disabled={!picked.length}
            onClick={() => void send()}
          >
            Send
          </SaveButton>
        </div>
      </form>
    </dialog>
  );
}

// ── cancelling ──

export function CancelDialog({
  job,
  timeZone,
  onClose,
  onCancelled,
}: {
  job: JobDetail;
  timeZone: string;
  onClose: () => void;
  onCancelled: (said: string) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [choice, setChoice] = useState('');
  const [note, setNote] = useState('');
  const [waive, setWaive] = useState(false);
  const [waiveWhy, setWaiveWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // QA-688: who hears about the visit (its address's site contact, else the customer), as Book texts them
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [text, setText] = useState(false);
  const [body, setBody] = useState<string | null>(null);
  const reason = cancelReason(choice, note);
  const textTo = customer ? visitTextTo(customer, job.locationId ?? null) : null;
  const canText = !!textTo && 'method' in textTo;
  const ready = !!reason && (!waive || !!waiveWhy.trim()) && (!text || !canText || !!body?.trim());
  // the text's words aren't in the key: a cancel sent again after a lost answer is the same cancel
  const save = useSaveKey(`cancel:${job.id}:${reason}:${waive ? waiveWhy.trim() : ''}`);
  const visit = visitToCancel(job.appointments);
  const when = visit
    ? `${dateText(visit.start, timeZone, { weekday: 'short', year: false, now: new Date() })}, ${windowText(visit.start, visit.end, timeZone)}`
    : null;
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  useEffect(() => {
    // read with the customer (customers.get): someone who can't, isn't offered the text; the send
    // itself is messages.send's, refused in words for someone who doesn't hold it
    let live = true;
    void Promise.all([
      client.call<Customer>('customers.get', { id: job.customerId }).catch(() => null),
      client
        .brand()
        .then((b) => b.name)
        .catch(() => null),
    ]).then(([c, name]) => {
      if (!live || !c) return;
      setCustomer(c);
      const to = visitTextTo(c, job.locationId ?? null);
      // on when someone there agreed to texts; the server checks consent and do-not-contact again
      if ('method' in to) {
        setText(true);
        setBody((b) => b ?? cancelText({ firstName: to.firstName, company: name, when }));
      }
    });
    return () => {
      live = false;
    };
    // the job's customer and address, once per dialog
  }, [job.customerId, job.locationId]);
  /** The cancel's text, after the cancel stands: refused or failed, the office is told, never misled. */
  const sendText = async (): Promise<string> => {
    if (!text || !textTo || !('method' in textTo) || !body?.trim()) return '';
    const who = textTo.firstName;
    const canSms = await client
      .call<{ sms: boolean; notSetUp: string }>('messages.channels', {})
      .catch(() => ({ sms: true, notSetUp: '' }));
    if (!canSms.sms) return ` ${canSms.notSetUp}`;
    try {
      await client.call(
        'messages.send',
        { contactMethodId: textTo.method.id, purpose: 'general', body: body.trim(), jobId: job.id },
        { idempotencyKey: `${save.key}:text` },
      );
      return ` ${who} is being texted that it’s cancelled.`;
    } catch (e) {
      return ` Not texted: ${errorText(e)}`;
    }
  };
  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{
        cancelledAppointmentIds: string[];
        tripCharge: { cents: number; waived: boolean } | null;
        voidedInvoices?: { number: string }[];
        issuedInvoices?: { number: string; balanceCents: number }[];
      }>(
        'jobs.cancel',
        { jobId: job.id, reason, ...(waive ? { waiveTripCharge: { reason: waiveWhy.trim() } } : {}) },
        { idempotencyKey: save.key },
      );
      const visits = r.cancelledAppointmentIds.length;
      const charge = r.tripCharge
        ? r.tripCharge.waived
          ? ' The trip charge was waived.'
          : ` The trip charge of ${money(r.tripCharge.cents)} was billed.`
        : '';
      const invoices = cancelInvoiceWords(r);
      const texted = await sendText();
      onCancelled(
        `Job #${job.number} cancelled${visits ? `, with ${visits === 1 ? 'its visit' : `${visits} visits`}` : ''}.${charge}${invoices ? ` ${invoices}` : ''}${texted}`,
      );
    } catch (e) {
      // The server's words: "This job has $X paid on it. Refund it or keep it as credit first." (QA-188)
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <dialog ref={ref} className="sheet ask job-ask" aria-labelledby="cancel-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy && ready) void cancel();
        }}
      >
        <h3 id="cancel-title">Cancel job #{job.number}?</h3>
        <p className="muted">
          {job.customerName} · {plainText(job.summary)}
        </p>
        <label>
          Reason (required)
          <select className="field" value={choice} onChange={(e) => setChoice(e.target.value)}>
            <option value="">Something else (write it below)</option>
            {CANCEL_REASONS.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
        </label>
        <label>
          {choice ? 'Note (optional)' : 'Why'}
          <textarea
            className="field"
            rows={2}
            maxLength={400}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        <label className="checkrow">
          <input type="checkbox" checked={waive} onChange={(e) => setWaive(e.target.checked)} /> Waive the
          trip charge, if there is one
        </label>
        {waive && (
          <label>
            Why it’s waived
            <input
              className="field"
              maxLength={500}
              value={waiveWhy}
              onChange={(e) => setWaiveWhy(e.target.value)}
            />
          </label>
        )}
        {textTo && 'method' in textTo && (
          <>
            <label className="checkrow">
              <input type="checkbox" checked={text} onChange={(e) => setText(e.target.checked)} /> Text{' '}
              {textTo.firstName} ({phoneText(textTo.method.value)}) that it’s cancelled
            </label>
            {text && (
              <label>
                The text
                <textarea
                  className="field"
                  rows={3}
                  maxLength={1600}
                  value={body ?? ''}
                  onChange={(e) => setBody(e.target.value)}
                />
              </label>
            )}
          </>
        )}
        {textTo && 'reason' in textTo && (
          <p className="muted small">No text: {textTo.reason}. Call them to say it’s cancelled.</p>
        )}
        <div className="row end">
          <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
            Keep the job
          </button>
          <SaveButton
            busy={busy}
            busyLabel="Cancelling…"
            error={error}
            disabled={!ready}
            className="btn primary danger"
            onClick={() => void cancel()}
          >
            Cancel the job
          </SaveButton>
        </div>
      </form>
    </dialog>
  );
}

/** One visit cancelled and the job kept (F-08, appointments.cancel): the reason is required. */
export function CancelVisitDialog({
  job,
  visit,
  onClose,
  onCancelled,
}: {
  job: JobDetail;
  visit: { id: string; when: string };
  onClose: () => void;
  onCancelled: (said: string) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [choice, setChoice] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reason = cancelReason(choice, note);
  const save = useSaveKey(`cancel-visit:${visit.id}:${reason}`);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call(
        'appointments.cancel',
        { appointmentId: visit.id, reason },
        { idempotencyKey: save.key },
      );
      onCancelled(`The visit on ${visit.when} is cancelled. Job #${job.number} stays open.`);
    } catch (e) {
      // the server's words: "Work has started on this visit…", "This is the job's only visit…"
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <dialog ref={ref} className="sheet ask job-ask" aria-labelledby="cancel-visit-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy && reason) void cancel();
        }}
      >
        <h3 id="cancel-visit-title">Cancel the visit on {visit.when}?</h3>
        <p className="muted">
          Job #{job.number} · {job.customerName}. The job and its other visits stay; a technician on the way
          is called back.
        </p>
        <label>
          Reason (required)
          <select className="field" value={choice} onChange={(e) => setChoice(e.target.value)}>
            <option value="">Something else (write it below)</option>
            {CANCEL_REASONS.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
        </label>
        <label>
          {choice ? 'Note (optional)' : 'Why'}
          <textarea
            className="field"
            rows={2}
            maxLength={400}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        <div className="row end">
          <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
            Keep the visit
          </button>
          <SaveButton
            busy={busy}
            busyLabel="Cancelling…"
            error={error}
            disabled={!reason}
            className="btn primary danger"
            onClick={() => void cancel()}
          >
            Cancel the visit
          </SaveButton>
        </div>
      </form>
    </dialog>
  );
}
